/**
 * ContratsConformite — Dashboard conformité :
 * heatmap des risques légaux par type + top contrats risqués.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ShieldAlert, ShieldCheck, ShieldQuestion, TrendingUp, ArrowRight,
  Sparkles, Loader2, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export default function ContratsConformite({ embedded = false } = {}) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/contracts/compliance-heatmap");
      setData(r.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading || !data) {
    return (
      <div className="p-8 text-center">
        <Loader2 className="h-6 w-6 mx-auto animate-spin text-slate-400" />
      </div>
    );
  }

  const scoreCls = data.average_risk_score >= 51 ? "text-red-700"
                    : data.average_risk_score >= 21 ? "text-amber-700"
                    : "text-emerald-700";

  return (
    <div className="space-y-6" data-testid="conformite-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-medium uppercase tracking-widest text-slate-500">Contrats</div>
          <h1 className="mt-1 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            Dashboard conformité
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Heatmap des risques légaux détectés par l'IA. Corrige d'abord les
            catégories qui touchent le plus de contrats.
          </p>
        </div>
      </div>

      {/* KPI band */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Portefeuille</div>
          <div className="mt-1 text-2xl font-semibold font-mono">{data.total_contracts}</div>
          <div className="text-[10px] text-slate-400 mt-1">contrats au total</div>
        </Card>
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Analysés IA</div>
          <div className="mt-1 text-2xl font-semibold font-mono">{data.analyzed_contracts}</div>
          <div className="text-[10px] text-slate-400 mt-1">{data.analyzed_pct}%</div>
        </Card>
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Score moyen</div>
          <div className={cn("mt-1 text-2xl font-semibold font-mono", scoreCls)}>
            {data.average_risk_score} /100
          </div>
          <div className="text-[10px] text-slate-400 mt-1">
            {data.average_risk_score >= 51 ? "Portefeuille risqué"
              : data.average_risk_score >= 21 ? "À surveiller" : "Sain"}
          </div>
        </Card>
        <Card className="p-4 bg-red-50/40">
          <div className="text-[10px] uppercase tracking-widest text-red-600">Critiques</div>
          <div className="mt-1 text-2xl font-semibold font-mono text-red-700">
            {data.distribution.critique}
          </div>
          <div className="text-[10px] text-red-500 mt-1">score ≥ 81</div>
        </Card>
        <Card className="p-4 bg-emerald-50/40">
          <div className="text-[10px] uppercase tracking-widest text-emerald-700">Sains</div>
          <div className="mt-1 text-2xl font-semibold font-mono text-emerald-700">
            {data.distribution.sain}
          </div>
          <div className="text-[10px] text-emerald-600 mt-1">score ≤ 20</div>
        </Card>
      </div>

      {/* Coverage banner if pct < 100 */}
      {data.analyzed_pct < 100 && data.total_contracts > 0 && (
        <Card className="p-3 border-blue-200 bg-blue-50/30 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm">
            <Sparkles className="h-4 w-4 text-blue-600" />
            <span className="text-slate-700">
              <b>{data.total_contracts - data.analyzed_contracts}</b> contrat(s) sans analyse IA —
              lance l'analyse pour couvrir 100% du portefeuille.
            </span>
          </div>
          <Button size="sm" variant="outline"
                  onClick={() => navigate("/app/contracts/intake")}
                  className="border-blue-300 text-blue-700 hover:bg-blue-100">
            Importer un contrat <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </Card>
      )}

      {/* Heatmap */}
      <Card className="p-5">
        <h2 className="text-lg font-semibold mb-1">Heatmap des risques légaux</h2>
        <p className="text-xs text-slate-500 mb-4">
          Cliquez sur un risque pour voir les contrats concernés (les plus
          urgents en premier).
        </p>
        <div className="space-y-2">
          {data.heatmap.map((r) => {
            const total = r.count;
            const maxCount = Math.max(...data.heatmap.map(h => h.count), 1);
            const barWidth = total ? Math.max(4, (total / maxCount) * 100) : 0;
            const empty = total === 0;
            return (
              <div key={r.code}
                   className={cn("rounded-lg border p-3",
                     empty ? "border-slate-100 bg-slate-50/30"
                           : "border-slate-200 hover:border-amber-300 hover:bg-amber-50/20 transition")}
                   data-testid={`heatmap-${r.code}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {empty ? <ShieldCheck className="h-4 w-4 text-emerald-500 shrink-0" />
                           : <ShieldAlert className="h-4 w-4 text-amber-600 shrink-0" />}
                    <span className={cn("text-sm font-medium truncate",
                                          empty ? "text-slate-500" : "text-slate-900")}>
                      {r.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    {r.high > 0 && <Badge className="bg-red-100 text-red-800 border-red-300 text-[10px]">{r.high} high</Badge>}
                    {r.medium > 0 && <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[10px]">{r.medium} med</Badge>}
                    {r.low > 0 && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px]">{r.low} low</Badge>}
                    <span className="text-slate-500 tabular-nums w-16 text-right">
                      {total} · {r.pct}%
                    </span>
                  </div>
                </div>
                {total > 0 && (
                  <div className="relative h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-amber-400 to-red-500"
                          style={{ width: `${barWidth}%` }} />
                  </div>
                )}
                {total > 0 && r.top_contracts?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {r.top_contracts.slice(0, 5).map((tc) => (
                      <button key={tc.id} onClick={() => navigate(`/app/contracts/${tc.id}`)}
                        className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-slate-200 hover:border-blue-400 hover:text-blue-700 text-slate-600"
                        data-testid={`heatmap-contract-${tc.id}`}>
                        {(tc.title || tc.party_name || "?").slice(0, 40)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {/* Top risky */}
      {data.top_risky_contracts?.length > 0 && (
        <Card className="p-5">
          <h2 className="text-lg font-semibold mb-1 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            Top {data.top_risky_contracts.length} contrats les plus risqués
          </h2>
          <p className="text-xs text-slate-500 mb-3">
            Priorise ces contrats pour la revue juridique.
          </p>
          <div className="space-y-1.5">
            {data.top_risky_contracts.map((c, i) => (
              <button key={c.id} onClick={() => navigate(`/app/contracts/${c.id}`)}
                className="w-full flex items-center gap-3 p-2.5 rounded-lg border border-slate-200 hover:border-red-300 hover:bg-red-50/30 transition text-left group"
                data-testid={`top-risky-${c.id}`}>
                <div className={cn("w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-xs font-bold tabular-nums",
                  c.risk_score >= 81 ? "bg-red-100 text-red-800"
                  : c.risk_score >= 51 ? "bg-orange-100 text-orange-800"
                  : "bg-amber-100 text-amber-800")}>
                  {c.risk_score}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 group-hover:text-blue-700 truncate">
                    #{i + 1} · {c.title || c.party_name}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    {c.party_name} · {c.risks_count} risque(s) détecté(s)
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-blue-600 shrink-0" />
              </button>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
